import React, { useState, useEffect } from "react";
import companyService from "@/services/companyService";
import { copyTasksToCustomerAssurance } from "@/services/frameworkService";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { notificationService, notify } from "@/services/notificationService";

interface Essential8State {
  enabled: "enabled" | "disabled";
  levels: string[];
}

const defaultEssential8: Essential8State = {
  enabled: "disabled",
  levels: [],
};

const COMPLIANCE_LEVEL_OPTIONS = [
  { value: "e8_level1", label: "Level 1" },
  { value: "e8_level2", label: "Level 2" },
  { value: "e8_level3", label: "Level 3" },
];

export default function Essential8Module() {
  const [essential8, setEssential8] = useState<Essential8State>(defaultEssential8);
  const [essential8Initial, setEssential8Initial] = useState<Essential8State | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchEssential8 = async () => {
      setIsLoading(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "complianceModules");
        const enabled = settings?.["essential8"]?.enabled ?? false;
        const levels = settings?.["essential8"]?.levels || [];
        const levelsArray = Array.isArray(levels) ? levels : (levels ? levels.split(',').map((l: string) => l.trim()).filter(Boolean) : []);
        setEssential8({ enabled: enabled ? "enabled" : "disabled", levels: levelsArray });
        setEssential8Initial({ enabled: enabled ? "enabled" : "disabled", levels: levelsArray });
      } catch (err) {
        toast.error("Failed to fetch Essential 8 settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchEssential8();
  }, []);

  const essential8Changed = essential8Initial && JSON.stringify(essential8) !== JSON.stringify(essential8Initial);

  const [open, setOpen] = useState(false);

  const handleLevelToggle = (level: string) => {
    setEssential8(prev => {
      const levelNumber = parseInt(level.replace('e8_level', ''));
      const newLevels: string[] = [];
      
      // Always set the clicked level as the highest level
      for (let i = 1; i <= levelNumber; i++) {
        newLevels.push(`e8_level${i}`);
      }
      
      return {
        ...prev,
        levels: newLevels
      };
    });
  };



  // Helper to get the highest selected level
  const getHighestLevel = () => {
    if (essential8.levels.length === 0) return 0;
    return Math.max(...essential8.levels.map(level => parseInt(level.replace('e8_level', ''))));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const wasDisabled = essential8Initial?.enabled === "disabled";
      const isNowEnabled = essential8.enabled === "enabled";
      
      // Save the settings first
      await companyService.saveCompanySettings(undefined, "complianceModules", {
        essential8: {
          enabled: isNowEnabled,
          levels: essential8.levels,
        },
      });
      
      // If Essential 8 is being enabled (was disabled, now enabled), copy tasks for each enabled level
      if (wasDisabled && isNowEnabled && essential8.levels.length > 0) {
        try {
          const customerId = window.companyID;
          
          if (!customerId) {
            throw new Error('Company ID not found');
          }
          
          // Copy tasks for each enabled level
          for (const level of essential8.levels) {
            const levelNumber = level.replace('e8_level', '');
            toast.info(`Copying Essential 8 Level ${levelNumber} tasks...`);
            
            await copyTasksToCustomerAssurance(
              customerId,
              'EssentialEight',
              undefined,
              {
                path: 'checkDetail.e8Details.e8LevelCoverage',
                value: levelNumber
              }
            );
          }
          
          toast.success(`Essential 8 settings saved and tasks copied for levels: ${essential8.levels.map(l => l.replace('e8_level', '')).join(', ')}`);
        } catch (taskError) {
          console.error('Error copying Essential 8 tasks:', taskError);
          toast.warning("Settings saved, but there was an error copying some tasks. Please check the console.");
          
        }
      } else {
        toast.success("Essential 8 settings saved");
      }
      
      setEssential8Initial({ ...essential8 });
    } catch (err) {
      console.error('Error saving Essential 8 settings:', err);
      toast.error("Failed to save Essential 8 settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-muted/30 p-6 rounded-lg border border-border space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-blue-500/10 p-3 rounded-full">
          <span role="img" aria-label="Essential 8">🛡️</span>
        </div>
        <div>
          <div className="font-semibold text-lg">Essential 8</div>
          <div className="text-xs text-muted-foreground">Australian Cyber Security Centre Essential 8 Module</div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Module Enabled</label>
        <Select
          value={essential8.enabled}
          onValueChange={val => {
            const newState = { ...essential8, enabled: val as Essential8State["enabled"] };
            // Auto-select Level 1 when enabling with no levels selected
            if (val === "enabled" && newState.levels.length === 0) {
              newState.levels = ["e8_level1"];
            }
            setEssential8(newState);
          }}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground mt-1">
          Indicates whether the Essential 8 Module is enabled or disabled.<br />
          For example, if set to <b>"Enabled,"</b> the Essential 8 Module is active.
        </div>
      </div>
      <div className="mt-6">
        <label className="block text-sm font-medium mb-1">Compliance Level</label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between"
              type="button"
              disabled={isLoading || isSaving}
            >
              {essential8.levels.length > 0
                ? (
                  <div className="truncate">
                    Level {getHighestLevel()} {getHighestLevel() > 1 ? '(includes 1-' + getHighestLevel() + ')' : ''}
                  </div>
                )
                : "Select levels..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] min-w-0 p-0">
            <Command filter={() => 1}>
              <CommandInput placeholder="Search levels..." />
              <CommandList className="max-h-[300px] overflow-auto">
                <CommandEmpty>No levels found.</CommandEmpty>
                <CommandGroup>
                  {COMPLIANCE_LEVEL_OPTIONS.map(opt => {
                    const levelNumber = parseInt(opt.value.replace('e8_level', ''));
                    const isSelected = essential8.levels.includes(opt.value);
                    
                    return (
                      <CommandItem
                        key={opt.value}
                        value={opt.value}
                        onSelect={() => {
                          handleLevelToggle(opt.value);
                          setOpen(true);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            isSelected ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {opt.label}
                        {isSelected && levelNumber < getHighestLevel() && (
                          <span className="ml-auto text-xs text-muted-foreground">
                            (included)
                          </span>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <div className="text-xs text-muted-foreground mt-1">
          Specifies the highest level of compliance required.<br />
          Selecting a level automatically includes all lower levels. For example, selecting <b>"Level 3"</b> includes Level 1 and Level 2.
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={handleSave} disabled={!essential8Changed || isSaving || isLoading}>
          {isSaving ? "Saving..." : "Save Essential 8 Settings"}
        </Button>
      </div>
    </div>
  );
}
